import { useEffect, useMemo, useState } from "react";
import { FiSearch, FiX, FiCalendar, FiLoader } from "react-icons/fi";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import {
    startOfWeek,
    differenceInWeeks,
    format,
} from "date-fns";
import { supabase } from "../../../config/supabase";

const getAustralianDate = () => {
    return new Date(
        new Date().toLocaleString("en-US", {
            timeZone: "Australia/Melbourne",
        })
    );
};

const getQuoteDate = (assignment) => {
    return (
        assignment?.quote_info?.expected_delivery_date ||
        assignment?.quote_info?.expected_finish_date ||
        null
    );
};

const getSearchText = (assignment) => {
    return [
        assignment?.unique_id,
        assignment?.quote_info?.child_unique_id,
        assignment?.customer_name,
        assignment?.customer_address,
        assignment?.carpenter_supervisor_name,
        ...(assignment?.job_designers || []).map(d => d?.name),
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
};

const mapJobQuoteToAssignment = (job, quote) => {
    return {
        ...job,

        quote_info: {
            id: quote.id,
            quoteId: quote.id,
            job_id: quote.job_id,
            quote_type: quote.quote_type,
            child_unique_id: quote.child_unique_id || null,
            expected_delivery_date: quote.expected_delivery_date || null,
            expected_finish_date: quote.expected_finish_date || null,
            design_status: job.is_cancelled ? "cancelled" : (quote.design_status || "waiting_assignment"),
            estimation_status: quote.estimation_status || null,
            production_status: quote.production_status || null,
            invoice_status: quote.invoice_status || null,
            invoice_state: quote.invoice_state || null,
            layout_only: quote.layout_only || false,
            on_hold: quote.on_hold || false,
            delivery_time_slot: quote.delivery_time_slot || null,
            timber_is_checked: quote.timber_is_checked || false,
            quick_notes: quote.quick_notes || [],
            quote_playground: quote.quote_playground || false,
            is_required: quote.is_required ?? true,
            sent_production: quote.sent_production || false,
            waiting_job: quote.waiting_job || false,
            sort_order: quote.sort_order ?? null,
            site_measure_date: quote.site_measure_date || null,
            site_measure_confirmed: quote.site_measure_confirmed || false,
        },

        job_quotes: job.job_quotes || [],
        job_designers: job.job_designers || [],
        job_estimators: job.job_estimators || null,
        job_production: job.job_production || [],
        job_deliveries: job.job_deliveries || [],
        designers: job.job_designers || [],
    };
};

const OutOfWeekJobSearch = ({
    companyId,
    quoteType,
    currentWeekStart,
    searchQuery,
    showOnlyMine,
    currentUserId,
    selectedUser,
    designStatusFilter,
    renderAssignmentCard,
    onJumpToWeek,
    cancelledFilter = "acitve_only"
}) => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    const trimmedQuery = searchQuery.trim();

    useEffect(() => {
        const fetchOutsideWeekJobs = async () => {
            if (!companyId || !quoteType || trimmedQuery.length < 2) {
                setResults([]);
                return;
            }

            setLoading(true);

            try {
                const currentWeekEnd = new Date(currentWeekStart);
                currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);

                const searchTerm = `%${trimmedQuery}%`;

                let queryBuilder = supabase
                    .from("jobs")
                    .select(`
                        *,
                        job_quotes (*),
                        job_designers (*),
                        job_estimators (*),
                        job_production (*),
                        job_deliveries (*)
                    `)
                    .eq("company_id", companyId)
                    .eq("deleted", false)
                    .or(
                        [
                            `unique_id.ilike.${searchTerm}`,
                            `customer_name.ilike.${searchTerm}`,
                            `customer_address.ilike.${searchTerm}`,
                            `carpenter_supervisor_name.ilike.${searchTerm}`,
                        ].join(",")
                    )
                    .limit(100);

                if (cancelledFilter === 'cancelled_only') {
                    queryBuilder = queryBuilder.eq('is_cancelled', true);
                } else if (cancelledFilter !== 'active_cancelled') {
                    queryBuilder = queryBuilder.or('is_cancelled.is.null,is_cancelled.eq.false');
                }

                const { data, error } = await queryBuilder;

                if (error) throw error;

                const mapped = [];

                for (const job of data || []) {
                    const quotes = (job.job_quotes || []).filter(q => {
                        if (q.quote_type !== quoteType) return false;
                        if (q.is_required === false) return false;
                        if (q.quote_playground === true) return false;

                        const date = q.expected_delivery_date || q.expected_finish_date;
                        if (!date) return false;

                        const quoteDate = new Date(date);
                        const isCurrentWeek =
                            quoteDate >= currentWeekStart &&
                            quoteDate <= currentWeekEnd;

                        if (isCurrentWeek) return false;

                        if (designStatusFilter && designStatusFilter !== "all") {
                            if ((q.design_status || "waiting_assignment") !== designStatusFilter) {
                                return false;
                            }
                        }

                        return true;
                    });

                    for (const quote of quotes) {
                        mapped.push(mapJobQuoteToAssignment(job, quote));
                    }
                }

                const query = trimmedQuery.toLowerCase();

                const filtered = mapped.filter(assignment => {
                    const textMatch =
                        getSearchText(assignment).includes(query) ||
                        assignment?.quote_info?.child_unique_id?.toLowerCase().includes(query);

                    if (!textMatch) return false;

                    if (showOnlyMine && currentUserId) {
                        const mine = assignment.job_designers?.some(d =>
                            d.user_id === currentUserId ||
                            d.designer_id === currentUserId
                        );

                        if (!mine) return false;
                    }

                    if (selectedUser) {
                        const assignedToSelected = assignment.job_designers?.some(d =>
                            d.user_id === selectedUser ||
                            d.designer_id === selectedUser
                        );

                        if (!assignedToSelected) return false;
                    }

                    return true;
                });

                filtered.sort((a, b) => {
                    return new Date(getQuoteDate(a)) - new Date(getQuoteDate(b));
                });

                setResults(filtered.slice(0, 20));
            } catch (error) {
                console.error("Out of week search error:", error);
                setResults([]);
            } finally {
                setLoading(false);
            }
        };

        const t = setTimeout(fetchOutsideWeekJobs, 350);
        return () => clearTimeout(t);
    }, [
        companyId,
        quoteType,
        trimmedQuery,
        currentWeekStart,
        showOnlyMine,
        currentUserId,
        selectedUser,
        designStatusFilter,
    ]);

    const resultIds = useMemo(() => {
        return results.map(a => a.quote_info?.id || a.id);
    }, [results]);

    if (!trimmedQuery || trimmedQuery.length < 2) return null;

    if (!loading && results.length === 0) return null;

    return (
        <div className="mt-4 rounded-xl border-2 border-blue-200 bg-blue-50/50 p-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h3 className="text-sm font-bold text-blue-900 flex items-center gap-2">
                        <FiSearch className="w-4 h-4" />
                        Jobs outside this week
                    </h3>
                    <p className="text-xs text-blue-700">
                        Results from other weeks matching your search.
                    </p>
                </div>

                {loading && (
                    <div className="flex items-center gap-2 text-xs text-blue-600 font-medium">
                        <FiLoader className="w-4 h-4 animate-spin" />
                        Searching
                    </div>
                )}
            </div>

            <SortableContext items={resultIds} strategy={rectSortingStrategy}>
                <div className="max-h-[360px] overflow-y-auto pr-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                        {results.map(assignment => {
                            const date = getQuoteDate(assignment);
                            const jobWeekStart = startOfWeek(new Date(date), { weekStartsOn: 1 });

                            return (
                                <div key={assignment.quote_info?.id || assignment.id}>
                                    {renderAssignmentCard(assignment)}

                                    <button
                                        type="button"
                                        onClick={() => onJumpToWeek(jobWeekStart)}
                                        className="mt-2 w-full flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-bold text-blue-700 hover:bg-blue-100 transition-colors"
                                    >
                                        <FiCalendar className="w-3.5 h-3.5" />
                                        Jump to week · {format(jobWeekStart, "dd MMM yyyy")}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </SortableContext>
        </div>
    );
};

export default OutOfWeekJobSearch;